import React, { useState } from 'react';
import Clock from 'lucide-react/dist/esm/icons/clock.js';
import Calendar from 'lucide-react/dist/esm/icons/calendar.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import LayoutGrid from 'lucide-react/dist/esm/icons/layout-grid.js';
import LayoutList from 'lucide-react/dist/esm/icons/layout-list.js';
import Lock from 'lucide-react/dist/esm/icons/lock.js';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '../../../contexts/TranslationContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useAuthConfig } from '../../../contexts/AuthConfigContext';
import { useGameType } from '../../../contexts/GameTypeContext';
import { useSiteConfig } from '../../../contexts/SiteConfigContext';
import { useDrawsPage } from './DrawsPageContext';
import { useDrawsCopy } from './useDrawsCopy';

// Display label for a draw's number — falls back to CTA game/week numbering.
const drawNumberLabel = (draw) => {
  if (draw.drawNumber) return `#${draw.drawNumber}`;
  if (draw.gameNumber && draw.weekNumber) return `G${draw.gameNumber} W${draw.weekNumber}`;
  if (draw.weekNumber) return `W${draw.weekNumber}`;
  return 'TBD';
};

export default function DrawsUpcomingGrid() {
  const { t } = useTranslations();
  const { activeTab, upcomingDraws, loading } = useDrawsPage();
  const { prizeLabel, weeklyPotLabel, estimatedValueLabel, prizeTBDLabel } = useDrawsCopy();
  const { gameType } = useGameType();
  const { nextDraw } = useSiteConfig();
  const [viewMode, setViewMode] = useState('tile');
  const { isLoggedIn } = useAuth();
  const { isGuestCheckoutEnabled } = useAuthConfig();
  const navigate = useNavigate();

  const handleBuyTickets = () => {
    if (isLoggedIn || isGuestCheckoutEnabled()) {
      navigate('/purchase');
    } else {
      sessionStorage.setItem('pendingPurchaseRoute', '/purchase');
      window.dispatchEvent(new CustomEvent('draws:requestLogin'));
    }
  };

  if (activeTab !== 'upcoming') return null;

  // Resolve the prize name to display for a draw. Falls back through the API
  // fields surfaced in DrawsPageContext.formatUpcoming. Returns null when no
  // prize name is configured.
  const resolvePrizeName = (draw) => {
    if (!draw.isPrize) return null;
    if (draw.prizeName && String(draw.prizeName).trim()) return String(draw.prizeName).trim();
    // For prize draws without a per-draw prize name, fall back to the first
    // non-empty localized prize description.
    const descs = draw.prizeDescriptions;
    if (descs && typeof descs === 'object') {
      for (const v of Object.values(descs)) {
        if (v && String(v).trim()) return String(v).trim();
      }
    }
    return null;
  };

  // Render the prize/jackpot block for the given draw. For prize draws, the
  // prize name is the headline and the dollar amount shows under "Est. Value".
  // For non-prize games (50/50, CTA), the dollar amount is the headline and
  // shows under the prizeLabel (e.g. "Current Jackpot").
  const renderAmountBlock = (draw, opts = {}) => {
    const bigClass = opts.bigClass ?? 'text-4xl font-bold text-gray-900 dark:text-white text-center';
    const smallClass = opts.smallClass ?? 'text-sm text-gray-500 dark:text-gray-400 mb-2 text-center';
    const subValueClass = opts.subValueClass ?? 'text-lg font-semibold text-gray-700 dark:text-gray-200 text-center';
    const alignClass = opts.alignClass ?? 'text-center';

    if (draw.isPrize) {
      const prizeName = resolvePrizeName(draw);
      const hasAmount = draw.jackpot > 0;
      return (
        <div className={`mb-6 ${alignClass}`}>
          {prizeName ? (
            <p className={bigClass}>{prizeName}</p>
          ) : (
            <p className={bigClass}>{prizeTBDLabel}</p>
          )}
          {hasAmount && (
            <p className={`${subValueClass} mt-1`}>
              <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">{estimatedValueLabel}:</span>
              ${draw.jackpot.toLocaleString()}
            </p>
          )}
        </div>
      );
    }

    const isChaseTheAce = gameType === 'chaseTheAce' || draw.gameType === 'chaseTheAce';
    const weeklyPotVal = draw.weeklyPrize > 0
      ? draw.weeklyPrize
      : (nextDraw?.weekly_prize ?? nextDraw?.secondary_prize ?? 0);

    return (
      <div className={`mb-6 ${alignClass}`}>
        <p className={smallClass}>{prizeLabel}</p>
        <p className={bigClass}>${draw.jackpot.toLocaleString()}</p>
        {isChaseTheAce && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700/60">
            <p className={smallClass}>{weeklyPotLabel}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white text-center">
              {weeklyPotVal > 0 ? `$${weeklyPotVal.toLocaleString()}` : (t('chaseTheAce.potBuilding', 'Pot Building'))}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white text-center flex-1">
          {t('draws.upcoming.heading')}
        </h2>
        {upcomingDraws.length > 0 && !loading && (
          <div className="inline-flex rounded-lg p-1 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setViewMode('tile')}
              className={`p-2 rounded-md transition-all ${
                viewMode === 'tile'
                  ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
              aria-label={t('draws.upcoming.viewTile')}
              title={t('draws.upcoming.viewTile')}
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-all ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
              aria-label={t('draws.upcoming.viewList')}
              title={t('draws.upcoming.viewList')}
            >
              <LayoutList className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="rounded-2xl p-6 text-gray-900 dark:text-gray-300 text-center">
          {t('draws.upcoming.loading')}
        </div>
      ) : upcomingDraws.length === 0 ? (
        <div className="w-full flex flex-col items-center justify-center py-16 text-center">
          <div className="bg-gradient-to-br from-blue-100 to-cyan-100 dark:from-blue-900/30 dark:to-cyan-900/30 p-5 rounded-full mb-6">
            <Calendar className="w-12 h-12 text-blue-500 dark:text-blue-400" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('draws.upcoming.empty.title')}</h3>
          <p className="text-gray-500 dark:text-gray-400 max-w-md">
            {t('draws.upcoming.empty.body')}
          </p>
        </div>
      ) : viewMode === 'tile' ? (
        <div className="flex flex-wrap justify-center gap-6 mb-12">
          {upcomingDraws.map((draw, index) => (
            <div
              key={draw.id}
              className="w-full sm:w-80 md:w-96 bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              style={{
                animation: `fade-in-up 0.6s ease-out ${index * 0.1}s both`,
              }}
            >
              <div className="bg-gradient-to-r from-gray-900 to-gray-800 dark:from-gray-700 dark:to-gray-600 p-6 text-center">
                <p className="text-sm font-bold text-gray-400 dark:text-gray-300 mb-2 tracking-wider">
                  {draw.day}
                </p>
                <p className="text-3xl md:text-5xl font-bold text-white mb-4">
                  {draw.date}
                </p>
                <div className="flex items-center justify-center gap-2 text-gray-300">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">{draw.time}</span>
                </div>
              </div>

              <div className="p-6">
                {renderAmountBlock(draw)}
                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-sm">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('draws.upcoming.drawNumber')}</p>
                    <p className="font-semibold text-gray-900 dark:text-white">{drawNumberLabel(draw)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('draws.upcoming.ticketsSold')}</p>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {draw.ticketsSold != null ? draw.ticketsSold.toLocaleString() : '—'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4 mb-12">
          {upcomingDraws.map((draw, index) => (
            <div
              key={draw.id}
              className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-md border border-gray-100 dark:border-gray-700 hover:shadow-lg transition-all duration-200"
              style={{
                animation: `fade-in-up 0.4s ease-out ${index * 0.05}s both`,
              }}
            >
              <div className="flex flex-col md:flex-row items-stretch">
                <div className="bg-gradient-to-r from-gray-900 to-gray-800 dark:from-gray-700 dark:to-gray-600 p-4 sm:p-5 md:w-44 lg:w-48 flex flex-col justify-center items-center text-white shrink-0">
                  <p className="text-xs font-bold text-gray-400 dark:text-gray-300 tracking-wider">
                    {draw.day}
                  </p>
                  <p className="text-xl lg:text-2xl font-bold text-white my-0.5">
                    {draw.date}
                  </p>
                  <div className="flex items-center gap-1.5 text-gray-300 mt-1">
                    <Clock className="w-3.5 h-3.5" />
                    <span className="text-xs">{draw.time}</span>
                  </div>
                </div>

                <div className="flex-1 p-4 sm:p-5 flex flex-col xl:flex-row items-stretch xl:items-center justify-between gap-4 sm:gap-6">
                  {/* Stats columns distributed evenly across the card */}
                  <div className={`flex-1 grid grid-cols-2 sm:grid-cols-3 ${(gameType === 'chaseTheAce' || draw.gameType === 'chaseTheAce') ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} items-center gap-4 sm:gap-6`}>
                    <div>
                      {draw.isPrize ? (
                        <>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 font-medium whitespace-nowrap">
                            {resolvePrizeName(draw) ? estimatedValueLabel : prizeLabel}
                          </p>
                          <p className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                            {resolvePrizeName(draw) ?? prizeTBDLabel}
                          </p>
                          {draw.jackpot > 0 && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              ${draw.jackpot.toLocaleString()}
                            </p>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 font-medium whitespace-nowrap">
                            {prizeLabel}
                          </p>
                          <p className="text-2xl font-bold text-gray-900 dark:text-white leading-none">
                            ${draw.jackpot.toLocaleString()}
                          </p>
                        </>
                      )}
                    </div>

                    {(gameType === 'chaseTheAce' || draw.gameType === 'chaseTheAce') && (
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 font-medium whitespace-nowrap">{weeklyPotLabel}</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white leading-none">
                          {(draw.weeklyPrize > 0 ? draw.weeklyPrize : (nextDraw?.weekly_prize ?? nextDraw?.secondary_prize ?? 0)) > 0
                            ? `$${(draw.weeklyPrize > 0 ? draw.weeklyPrize : (nextDraw?.weekly_prize ?? nextDraw?.secondary_prize ?? 0)).toLocaleString()}`
                            : (t('chaseTheAce.potBuilding', 'Pot Building'))}
                        </p>
                      </div>
                    )}

                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 font-medium whitespace-nowrap">{t('draws.upcoming.drawNumber')}</p>
                      <p className="text-base font-semibold text-gray-900 dark:text-white leading-none">
                        {drawNumberLabel(draw)}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 font-medium whitespace-nowrap">{t('draws.upcoming.ticketsSold')}</p>
                      <p className="text-base font-semibold text-gray-900 dark:text-white leading-none">
                        {draw.ticketsSold != null ? draw.ticketsSold.toLocaleString() : '—'}
                      </p>
                    </div>

                    <div className="flex items-center">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs font-semibold whitespace-nowrap">
                        <Clock className="w-3.5 h-3.5" />
                        {t('draws.upcoming.statusUpcoming')}
                      </span>
                    </div>
                  </div>

                  {/* Actions button */}
                  <div className="w-full xl:w-auto shrink-0 flex items-center justify-end xl:pl-4 xl:border-l xl:border-gray-100 dark:xl:border-gray-700/60">
                    {index === 0 ? (
                      <button
                        onClick={handleBuyTickets}
                        className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all hover:shadow-lg text-sm whitespace-nowrap active:scale-[0.98]"
                      >
                        <div className="flex items-center justify-center gap-2">
                          <Ticket className="w-4 h-4" />
                          <span>{t('draws.upcoming.buyTickets')}</span>
                        </div>
                      </button>
                    ) : (
                      <button
                        disabled
                        className="w-full sm:w-auto bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-6 py-2.5 rounded-lg font-semibold cursor-not-allowed text-sm whitespace-nowrap"
                      >
                        <div className="flex items-center justify-center gap-2">
                          <Lock className="w-4 h-4" />
                          <span>{t('draws.upcoming.comingSoon', 'Coming Soon')}</span>
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
